import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  type ReactNode,
} from 'react';
import type { AuthResponse } from '@ether-decks/shared';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface User {
  id: string;
  display_name: string;
  is_admin: boolean;
}

interface AuthContextValue {
  user: User | null;
  token: string | null;
  isAdmin: boolean;
  isAuthenticated: boolean;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string, displayName: string) => Promise<void>;
  logout: () => Promise<void>;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const STORAGE_KEY = 'ether_decks_token';
const API_BASE = import.meta.env.VITE_API_URL || '';

async function apiFetch<T>(
  path: string,
  options: RequestInit = {},
): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
    ...options,
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({ message: res.statusText }));
    throw new Error(body.message ?? `Request failed (${res.status})`);
  }
  return res.json() as Promise<T>;
}

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

const AuthContext = createContext<AuthContextValue | null>(null);

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  // Persist / clear token in localStorage
  const persist = useCallback((t: string | null) => {
    setToken(t);
    if (t) {
      localStorage.setItem(STORAGE_KEY, t);
    } else {
      localStorage.removeItem(STORAGE_KEY);
    }
  }, []);

  // Validate an existing token on mount
  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) {
      setLoading(false);
      return;
    }

    let cancelled = false;

    // Validate stored token by fetching current user's profile.
    // We use the users/:id endpoint via a lightweight session lookup.
    // First try /api/auth/me (ideal), fall back to decoding stored user info.
    const storedUser = localStorage.getItem(STORAGE_KEY + '_user');
    if (storedUser) {
      try {
        const parsed = JSON.parse(storedUser);
        setUser(parsed);
        setToken(stored);
        setLoading(false);
        return;
      } catch {
        // fall through
      }
    }

    // No cached user data - try to validate token against a lightweight endpoint
    apiFetch<{ decks: unknown[] }>('/api/decks', {
      headers: { Authorization: `Bearer ${stored}` },
    })
      .then(() => {
        // Token is valid but we don't have user data - clear and require re-login
        if (cancelled) return;
        localStorage.removeItem(STORAGE_KEY);
      })
      .catch(() => {
        if (cancelled) return;
        localStorage.removeItem(STORAGE_KEY);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  // ------ Auth actions ------------------------------------------------------

  const login = useCallback(
    async (email: string, password: string) => {
      const data = await apiFetch<{ user: { id: string; display_name: string; is_admin: boolean | number }; token: string }>('/api/auth/login', {
        method: 'POST',
        body: JSON.stringify({ email, password }),
      });
      const u = {
        id: data.user.id,
        display_name: data.user.display_name,
        is_admin: !!data.user.is_admin,
      };
      persist(data.token);
      localStorage.setItem(STORAGE_KEY + '_user', JSON.stringify(u));
      setUser(u);
    },
    [persist],
  );

  const register = useCallback(
    async (email: string, password: string, displayName: string) => {
      const data = await apiFetch<{ user: { id: string; display_name: string; is_admin: boolean | number }; token: string }>('/api/auth/register', {
        method: 'POST',
        body: JSON.stringify({ email, password, display_name: displayName }),
      });
      const u = {
        id: data.user.id,
        display_name: data.user.display_name,
        is_admin: !!data.user.is_admin,
      };
      persist(data.token);
      localStorage.setItem(STORAGE_KEY + '_user', JSON.stringify(u));
      setUser(u);
    },
    [persist],
  );

  const logout = useCallback(async () => {
    try {
      if (token) {
        await fetch(`${API_BASE}/api/auth/logout`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
        });
      }
    } catch {
      // Best-effort server-side logout; always clear locally.
    }
    persist(null);
    localStorage.removeItem(STORAGE_KEY + '_user');
    setUser(null);
  }, [token, persist]);

  // ------ Provide -----------------------------------------------------------

  const value: AuthContextValue = {
    user,
    token,
    isAdmin: user?.is_admin ?? false,
    isAuthenticated: !!user,
    loading,
    login,
    register,
    logout,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return ctx;
}
